import React, { useState, useRef, useEffect, useCallback } from 'react';

function CustomTextEditor({ value, onChange, placeholder = "Describe your venue..." }) {
  const editorRef = useRef(null);
  const [formats, setFormats] = useState({ bold: false, italic: false, align: 'left', header: null });

  // Save selection before button click, restore after
  const [savedSelection, setSavedSelection] = useState(null);

  const saveSelection = useCallback(() => {
    if (window.getSelection) {
      setSavedSelection(window.getSelection().getRangeAt(0));
    }
  }, []);

  const restoreSelection = useCallback(() => {
    if (savedSelection && editorRef.current) {
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(savedSelection);
    }
  }, [savedSelection]);

  // Update formats on selection change
  useEffect(() => {
    const checkFormats = () => {
      if (editorRef.current) {
        const selection = window.getSelection();
        if (selection.rangeCount > 0) {
          const range = selection.getRangeAt(0);
          const container = range.commonAncestorContainer;
          const el = container.nodeType === Node.TEXT_NODE ? container.parentElement : container;
          
          setFormats({
            bold: document.queryCommandState('bold'),
            italic: document.queryCommandState('italic'),
            align: document.queryCommandValue('justify') || 'left',
            header: document.queryCommandValue('formatBlock')?.toLowerCase().match(/h([1-6])/i)?.[1] || null
          });
        }
      }
    };

    document.addEventListener('selectionchange', checkFormats);
    return () => document.removeEventListener('selectionchange', checkFormats);
  }, []);

  const applyFormat = (command, value = null) => {
    saveSelection(); // Save before click
    editorRef.current?.focus();
    restoreSelection(); // Restore selection
    document.execCommand(command, false, value === null ? undefined : value);
    onChange(editorRef.current?.innerHTML || '');
    setTimeout(() => checkFormats(), 0); // Update states
  };

  const checkFormats = () => {
    // Implementation moved to useEffect
  };

  // Sync external value changes
  useEffect(() => {
    if (editorRef.current && editorRef.current.innerHTML !== value) {
      editorRef.current.innerHTML = value || '';
    }
  }, [value]);

  return (
    <div className="w-full bg-white border border-gray-300 rounded-lg shadow-sm overflow-hidden focus-within:ring-2 focus-within:ring-purple-500">
      {/* Toolbar */}
      <div className="bg-gray-50 px-3 py-2 border-b border-gray-200 flex flex-wrap gap-1 items-center">
        {/* Headings */}
        <div className="flex gap-1 text-xs">
          <button
            onMouseDown={saveSelection} // Prevent selection loss
            onClick={() => applyFormat('formatBlock', 'h1')}
            className={`px-2 py-1 rounded font-medium transition-all ${
              formats.header === '1' ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'text-gray-700 hover:bg-gray-200'
            }`}
          >
            H1
          </button>
          {[2,3,4,5,6].map((level) => (
            <button
              key={level}
              onMouseDown={saveSelection}
              onClick={() => applyFormat('formatBlock', `h${level}`)}
              className={`px-2 py-1 rounded font-medium transition-all ${
                formats.header === level.toString() ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'text-gray-700 hover:bg-gray-200'
              }`}
            >
              H{level}
            </button>
          ))}
        </div>
        <span className="w-px h-4 bg-gray-300 mx-2" />
        {/* Bold/Italic */}
        <button onMouseDown={saveSelection} onClick={() => applyFormat('bold')}
          className={`p-2 rounded transition-all ${formats.bold ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'text-gray-700 hover:bg-gray-200'}`}
        >
          <b>B</b>
        </button>
        <button onMouseDown={saveSelection} onClick={() => applyFormat('italic')}
          className={`p-2 rounded transition-all ${formats.italic ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'text-gray-700 hover:bg-gray-200'}`}
        >
          <i>I</i>
        </button>
        <span className="w-px h-4 bg-gray-300 mx-2" />
        {/* Alignment */}
        {['left', 'center', 'right'].map((align) => (
          <button
            key={align}
            onMouseDown={saveSelection}
            onClick={() => applyFormat(`justify${align.charAt(0).toUpperCase() + align.slice(1)}`)}
            className={`p-2 rounded transition-all ${formats.align === align ? 'bg-gradient-to-r from-purple-600 to-pink-600 text-white' : 'text-gray-700 hover:bg-gray-200'}`}
          >
            {align === 'left' ? '◀' : align === 'center' ? '▻' : '▶'}
          </button>
        ))}
        <span className="w-px h-4 bg-gray-300 mx-2" />
        <button onMouseDown={saveSelection} onClick={() => applyFormat('insertUnorderedList')} 
          className="p-2 rounded text-gray-700 hover:bg-gray-200">•</button>
        <button onMouseDown={saveSelection} onClick={() => applyFormat('removeFormat')} 
          className="p-2 rounded text-gray-500 hover:bg-gray-200 text-xs font-medium">Clear</button>
      </div>
      
      {/* Editor */}
      <div
        ref={editorRef}
        contentEditable="true"
        suppressContentEditableWarning
        className="w-full px-4 py-4 min-h-[120px] outline-none text-gray-900 focus:bg-gray-50"
        placeholder={placeholder}
        onBlur={() => onChange(editorRef.current?.innerHTML || '')}
        onInput={() => onChange(editorRef.current?.innerHTML || '')}
      />
    </div>
  );
}

export default CustomTextEditor;
